import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U2101";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  EXPORT_EXCEL: string;
  FIND_LOGICAL_UNIT_CATEGORY: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  FIND_LOGICAL_UNIT_CATEGORY: `${LOGICALUNIT_URL}/findLogicalUnitCategory`,
};

export interface LogicalUnitCategoryRef {
  id: string;
  code?: string;
  labelFr?: string;
  labelEn?: string;
  sortOrder?: number;
  version?: number;
}

export interface LogicalUnit {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  logicalUnitTypes: string[];
  logicalUnitActions: LogicalUnitAction[];
  logicalUnitPersonalizations: LogicalUnitPersonalization[];
  logicalUnitCategory?: LogicalUnitCategoryRef | null;
  logicalUnitCategoryId?: string | null;
  logicalUnitCategoryCode?: string | null;
  logicalUnitCategoryLabelFr?: string | null;
  logicalUnitCategoryLabelEn?: string | null;
}

export interface LogicalUnitAction {
  id: string;
  idChecked: boolean;
  logicalUnitActionCode: string;
  logicalUnitActionDescription: string;
  logicalUnitActionTypes: string[];
}

export interface LogicalUnitPersonalization {
  id: string;
  logicalUnitPersonalizationCode: string;
  logicalUnitPersonalizationDescription: string;
  logicalUnitPersonalizationValue: string;
}

export const emptyLogicalUnit: LogicalUnit = {
  id: "",
  logicalUnitCode: "",
  logicalUnitDescription: "",
  logicalUnitTypes: [],
  logicalUnitActions: [],
  logicalUnitPersonalizations: [],
  logicalUnitCategory: null,
};

export const emptyLogicalUnitAction: LogicalUnitAction = {
  id: "",
  idChecked: false,
  logicalUnitActionCode: "",
  logicalUnitActionDescription: "",
  logicalUnitActionTypes: [],
};

export const emptyLogicalUnitPersonalization: LogicalUnitPersonalization = {
  id: "",
  logicalUnitPersonalizationCode: "",
  logicalUnitPersonalizationDescription: "",
  logicalUnitPersonalizationValue: "",
};

import type { AxiosInstance } from "axios";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: LogicalUnit) => {
  return axios.post(LogicalUnitEndpoints.CREATE, data);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: LogicalUnit) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), data);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: any) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export type U2101AutoCompletCriterias = { fieldName: string; value: unknown }[];

export interface U2101FindLogicalUnitCategoryItem {
  id: string;
  code?: string;
  labelFr?: string;
  labelEn?: string;
  sortOrder?: number;
}

export const doFindLogicalUnitCategory = (axios: AxiosInstance, criterias: U2101AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FIND_LOGICAL_UNIT_CATEGORY, criterias);
};

/** Payload create/update : catégorie optionnelle par id ou null pour retrait. */
export function buildLogicalUnitSavePayload(data: LogicalUnit): LogicalUnit {
  const { logicalUnitCategory, ...rest } = data;
  const categoryPayload =
    logicalUnitCategory && logicalUnitCategory.id
      ? { id: logicalUnitCategory.id }
      : logicalUnitCategory === null
        ? null
        : undefined;
  return {
    ...rest,
    ...(categoryPayload !== undefined ? { logicalUnitCategory: categoryPayload as LogicalUnitCategoryRef | null } : {}),
  } as LogicalUnit;
}

export function luHandleAppiError(t: TranslateFn, NS: string, err: any, errorResult: U2000ApiErrorResult) {
  if (err.errorCode === "BU210101") {
    errorResult.fieldErrors["logicalUnitTypes"] = t("errLogicalUnitTypeRequired", { ns: NS });
  } else if (err.errorCode === "BU210102") {
    errorResult.otherErrors["logicalUnitActions"] = t("errLogicalUnitActionDuplicate", { ns: NS });
  } else if (err.errorCode === "BU210103") {
    const bu210103Errors: string[] = [];
    err.parameters?.forEach((param: any) => {
      if (param.name === "logicalUnitActionCode") {
        bu210103Errors.push(t("errlogicalUnitActionTypeMissing", { ns: NS, codeAction: param.value }));
      }
    });
    errorResult.otherErrors["logicalUnitActions"] = bu210103Errors;
  } else if (err.errorCode === "BU210105") {
    errorResult.fieldErrors["logicalUnitCategory"] = t("errLogicalUnitCategoryNotFound", { ns: NS });
  }
}
